import { useQuery } from '@tanstack/react-query'
import { api, type Verdict } from '../lib/api'
import { RateCard, StackedBars } from '../components/Chart'

/**
 * Deliverability health monitor. One screen that answers "is my email set up to arrive?" —
 * an overall verdict, bounce/complaint rates against the thresholds mailbox providers enforce,
 * per-domain SPF/DKIM/DMARC status, sending-IP reputation, and recent volume.
 */
function VerdictPill({ v }: { v: Verdict }) {
  const cls = v === 'good' ? 'ok' : v === 'warning' ? 'pending' : 'failed'
  const label = v === 'good' ? 'Healthy' : v === 'warning' ? 'Needs attention' : 'Critical'
  return <span className={`badge ${cls}`}>{label}</span>
}

function authBadge(s: string) {
  const cls = s === 'ok' ? 'ok' : s === 'missing' ? 'failed' : 'pending'
  return <span className={`badge ${cls}`}>{s}</span>
}

export function Health() {
  const { data, isLoading } = useQuery({ queryKey: ['health'], queryFn: () => api.health.get(30) })

  if (isLoading) return <div className="skeleton" style={{ height: 300 }} />
  if (!data) return <div className="empty"><p>Health data unavailable.</p></div>

  const d = data.deliverability
  const labels = data.volume.map(v => v.date)
  const volSeries = [
    { key: 'delivered', label: 'Delivered', colour: 'var(--success)', values: data.volume.map(v => v.delivered) },
    { key: 'bounced', label: 'Bounced', colour: 'var(--danger)', values: data.volume.map(v => v.bounced) },
    { key: 'deferred', label: 'Deferred', colour: 'var(--warning)', values: data.volume.map(v => v.deferred) },
  ]

  return (
    <div>
      <div className="spread" style={{ alignItems: 'center' }}>
        <h1>Health monitor</h1>
        <VerdictPill v={data.overall} />
      </div>
      <p className="lede">Last {data.window_days} days. The checks mailbox providers actually enforce.</p>

      <div className="stats" style={{ marginBottom: 24 }}>
        <RateCard label="Bounce rate" value={+(d.bounce_rate * 100).toFixed(2)}
                  warn={d.bounce_warn * 100} critical={d.bounce_crit * 100} invert
                  hint={`${d.bounced} of ${d.attempted} attempted`} />
        <RateCard label="Complaint rate" value={+(d.complaint_rate * 100).toFixed(3)}
                  warn={d.complaint_warn * 100} critical={d.complaint_crit * 100} invert
                  hint={`${d.complained} of ${d.delivered} delivered`} />
        <div className="rate ok">
          <div className="rate-label">Delivered</div>
          <div className="rate-value">{d.delivered.toLocaleString()}</div>
          <div className="rate-hint">in the last {data.window_days} days</div>
        </div>
      </div>

      <h2>Domain authentication</h2>
      {data.domains.length === 0 ? (
        <div className="empty"><p>No domains yet. Add and verify a domain to send.</p></div>
      ) : (
        <div className="card flush" style={{ marginBottom: 24 }}>
          <table className="data">
            <thead><tr><th>Domain</th><th>SPF</th><th>DKIM</th><th>DMARC</th><th>Return-path</th><th>Verdict</th></tr></thead>
            <tbody>
              {data.domains.map(dom => (
                <tr key={dom.name}>
                  <td className="strong">{dom.name}
                    {dom.issues.length > 0 && <div className="small muted">{dom.issues[0]}</div>}
                  </td>
                  <td>{authBadge(dom.spf)}</td>
                  <td>{authBadge(dom.dkim)}</td>
                  <td>{authBadge(dom.dmarc)}</td>
                  <td>{authBadge(dom.return_path)}</td>
                  <td><VerdictPill v={dom.verdict} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.sending_ips.length > 0 && (
        <>
          <h2>Sending IPs</h2>
          <div className="card flush" style={{ marginBottom: 24 }}>
            <table className="data">
              <thead><tr><th>Address</th><th>Status</th><th>Reputation</th><th>Blocklist</th></tr></thead>
              <tbody>
                {data.sending_ips.map(ip => (
                  <tr key={ip.address}>
                    <td className="mono">{ip.address}</td>
                    <td>{ip.status}</td>
                    <td>{ip.reputation}</td>
                    <td>{ip.blocklisted ? <span className="badge failed">listed</span> : <span className="badge ok">clean</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2>Volume</h2>
      {data.volume.length === 0
        ? <div className="empty"><p>No sending activity in this window yet.</p></div>
        : <div className="card"><StackedBars labels={labels} series={volSeries} /></div>}
    </div>
  )
}
